/**
 * Normalizes lead-time line-item properties on /cart/add.js before merge or passthrough.
 * Serializes /cart/add.js so two rapid adds cannot both miss an existing line (same variant).
 */

function clampNonNegativeInventory(value) {
  const n = parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

function normalizeLineInventoryProperties(formData) {
  const clamped = clampNonNegativeInventory(formData.get('properties[_inventory_quantity]'));
  formData.set('properties[_inventory_quantity]', String(clamped));

  let cs = formData.get('properties[_continue_selling]');
  cs = (cs == null ? '' : String(cs)).trim().toLowerCase();
  const normalized = cs === 'continue' || cs === 'true' ? 'continue' : 'deny';
  formData.set('properties[_continue_selling]', normalized);
}

function rebuildFetchConfig(config, formData, wasFormDataBody) {
  const base = config && typeof config === 'object' ? Object.assign({}, config) : {};
  if (wasFormDataBody) {
    base.body = formData;
    return base;
  }
  const params = new URLSearchParams();
  for (const [key, value] of formData.entries()) {
    params.append(key, value);
  }
  const headers = new Headers(config && config.headers ? config.headers : undefined);
  headers.set('Content-Type', 'application/x-www-form-urlencoded');
  base.body = params.toString();
  base.headers = headers;
  return base;
}

document.addEventListener('DOMContentLoaded', function () {
  const originalFetch = window.fetch.bind(window);
  /** One /cart/add.js at a time so merge-by-variant always sees the latest cart. */
  let cartAddGate = Promise.resolve();

  async function handleCartAddIntercept(urlInput, config) {
    const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart.js';
    const cartChangeUrl =
      (window.theme && window.theme.routes && window.theme.routes.cartChange) || '/cart/change.js';

    try {
      const cartResponse = await originalFetch(cartUrl);
      const cart = await cartResponse.json();

      if (!(config.body instanceof FormData) && typeof config.body !== 'string') {
        return originalFetch.apply(window, [urlInput, config]);
      }

      let formData;
      const wasFormDataBody = config.body instanceof FormData;
      if (wasFormDataBody) {
        formData = config.body;
      } else {
        const parsed = new URLSearchParams(config.body);
        formData = new FormData();
        for (const [key, value] of parsed) {
          formData.append(key, value);
        }
      }

      normalizeLineInventoryProperties(formData);

      const variantId = parseInt(formData.get('id'), 10);
      const quantity = parseInt(formData.get('quantity'), 10);
      const inventoryQuantity = clampNonNegativeInventory(formData.get('properties[_inventory_quantity]'));
      const continueSellingNorm = formData.get('properties[_continue_selling]');
      const continueSelling = continueSellingNorm === 'continue';

      const inStockLeadTime = formData.get('properties[_in_stock_lead_time]');
      const outOfStockLeadTime = formData.get('properties[_out_of_stock_lead_time]');

      const existingItem = cart.items.find(function (item) {
        return parseInt(item.variant_id, 10) === variantId;
      });

      if (existingItem) {
        const newTotalQuantity = existingItem.quantity + quantity;

        if (!continueSelling && inventoryQuantity < newTotalQuantity) {
          const errorMessage = "We don't have enough stock to fulfill your request.";
          return new Response(
            JSON.stringify({
              status: 422,
              description: errorMessage,
              message: errorMessage,
            }),
            {
              status: 422,
              headers: { 'Content-Type': 'application/json' },
            }
          );
        }

        return originalFetch(cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingItem.key,
            quantity: newTotalQuantity,
            properties: Object.assign({}, existingItem.properties || {}, {
              _inventory_quantity: String(inventoryQuantity),
              _continue_selling: continueSellingNorm,
              _in_stock_lead_time: inStockLeadTime,
              _out_of_stock_lead_time: outOfStockLeadTime,
            }),
          }),
        });
      }

      return originalFetch(urlInput, rebuildFetchConfig(config, formData, wasFormDataBody));
    } catch (error) {
      console.error('Error in delivery timeline calculation:', error);
      return originalFetch.apply(window, [urlInput, config]);
    }
  }

  window.fetch = async function (...args) {
    const [urlInput, configArg] = args;
    const config = configArg || {};

    let urlString;
    try {
      if (urlInput instanceof URL) {
        urlString = urlInput.toString();
      } else if (typeof urlInput === 'string') {
        urlString = urlInput;
      } else if (urlInput && typeof urlInput.toString === 'function') {
        urlString = urlInput.toString();
      } else {
        return originalFetch.apply(this, args);
      }

      if (
        urlString.includes('.glb') ||
        urlString.includes('/3d/models/') ||
        (config.headers && config.headers['model-viewer-request'] === 'true')
      ) {
        return originalFetch.apply(this, args);
      }

      if (!urlString.includes('/cart/add.js')) {
        return originalFetch.apply(this, args);
      }

      const previous = cartAddGate;
      let release;
      cartAddGate = new Promise(function (resolve) {
        release = resolve;
      });

      await previous;

      try {
        return await handleCartAddIntercept(urlInput, config);
      } finally {
        release();
      }
    } catch (error) {
      console.error('Error handling URL in fetch interceptor:', error);
      return originalFetch.apply(this, args);
    }
  };
});

document.addEventListener('DOMContentLoaded', function () {
  const modelViewer = document.querySelector('model-viewer');
  if (modelViewer) {
    console.log('Model viewer found:', modelViewer);
    modelViewer.addEventListener('load', function () {
      console.log('Model loaded successfully');
    });
    modelViewer.addEventListener('error', function (error) {
      console.error('Model viewer error:', error);
    });
  }
});
